import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { getIntegrationDisplayName, INTEGRATIONS_WITH_DYNAMIC_SCHEMA, IntegrationServiceId } from '../../../common/integrations'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { Button } from '../../../components/Button/Button'

import { deselectKeys, selectSegmentLoadingStates, selectSelectedKeys, setSelectedKeys } from '../../../store/meetingSummary/contactUpdate'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectDocumentProspectingDetailsFormLoading,
  selectDocumentProspectingDetailsFormProps,
  selectDocumentProspectingDetailsFormValues,
  selectDocumentProspectingIntegrationContactErrors,
  selectDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'

import {
  DocumentProspectingDetailsFormProps,
  DocumentProspectingStep,
  DocumentProspectingStepProps,
  ProspectingDetailsFormValues,
  SegmentedDocumentProspectingDetailsFormProps,
  SegmentedProspectingDetailsFormValues,
} from '../../../models/DocumentProspecting'

import { UpdateExternalContactForm } from '../../UpdateExternalContact/UpdateExternalContactForm'
import { gatherAllChildSuccessfulFields, getAllKeysInData } from '../../UpdateExternalContact/utils'
import { SegmentedExternalContactUpdateClickNavigationSection } from '../../UpdateExternalContact/SegmentedExternalContactUpdateClickNavigationSection'
import { NavigationSidebarSection } from '../../../components/NavigationWithSideBar/NavigationSidebar'
import { ClickNavigation, ClickNavigationSection } from '../../../components/NavigationWithSideBar/ClickNavigation'

import { useProspectingDetailsForm } from './hooks'
import { useSectionNavigation } from '../../UpdateExternalContact/hooks'

const _PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID = 'data'

const _CURRENT_STEP = DocumentProspectingStep.DETAILS_FORM

interface ProspectingDetailsFormRef {
  submit: () => Promise<void>
}

export const ProspectingDetailsForm: React.FC<DocumentProspectingStepProps> = ({ activeStep, onComplete: _onComplete }) => {
  const { tracked } = useTracking()

  const serviceId = useAppSelector(selectDocumentProspectingServiceId)
  const values = useAppSelector(selectDocumentProspectingDetailsFormValues)
  const props = useAppSelector(selectDocumentProspectingDetailsFormProps)
  const isLoading = useAppSelector(selectDocumentProspectingDetailsFormLoading)

  const formRef = useRef<ProspectingDetailsFormRef | null>(null)

  const { submitFormValues, submitLoading } = useProspectingDetailsForm()

  const isActive = activeStep === _CURRENT_STEP

  const onComplete = tracked(_onComplete, { eventName: TrackEvent.DOCUMENT_PROSPECTING_COMPLETED })

  const onSubmit = useCallback(
    async (detailsFormValues: ProspectingDetailsFormValues) => {
      const isSuccess = await submitFormValues(detailsFormValues)
      if (!isSuccess) {
        return
      }
      onComplete()
    },
    [onComplete, submitFormValues],
  )

  const _handleSubmit = async () => {
    if (!formRef.current) {
      return
    }

    await formRef.current.submit()
  }

  const handleSubmit = tracked(_handleSubmit, { eventName: TrackEvent.DOCUMENT_PROSPECTING_FIELDS_CONFIRMED })

  if (!isActive) {
    return (
      <div className='d-flex align-items-center gap-3'>
        <div className='z-1 border border-4 border-start-0 border-end-0 border-white'>
          <div className='document-prospecting-step-icon-wrapper rounded-2 d-flex align-items-center justify-content-center zocks-border-color-gray-200 zocks-bg-color-gray-100 border'>
            <i className='bi bi-list-check zocks-text-gray-500' />
          </div>
        </div>
        <div className='d-flex align-items-center document-prospecting-step-header-wrapper'>
          <h5 className='zocks-text-gray-500'>Select fields to save</h5>
        </div>
      </div>
    )
  }

  if (!serviceId) {
    return null
  }

  const integrationDisplayName = getIntegrationDisplayName(serviceId)

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex gap-3 align-items-start'>
        <div className='z-1 border border-4 border-start-0 border-end-0 border-white'>
          <div className='document-prospecting-step-icon-wrapper rounded-2 d-flex align-items-center justify-content-center zocks-border-color-gray-200 zocks-bg-color-gray-100 border'>
            <i className='bi bi-list-check' />
          </div>
        </div>
        <div className='d-flex flex-column'>
          <div className='d-flex align-items-center document-prospecting-step-header-wrapper'>
            <h5>Select fields to save</h5>
          </div>
          <span className='paragraph-small'>Choose and review fields for {integrationDisplayName}.</span>
        </div>
      </div>
      <div className='d-flex flex-column gap-4 zocks-pl-3.0'>
        {!values || !props || isLoading ? (
          <div className='skeleton d-flex flex-column align-items-center justify-content-center zocks-py-4.0'>
            <span className='paragraph-medium'>Loading…</span>
          </div>
        ) : (
          <ProspectingDetailsFormContent ref={formRef} serviceId={serviceId} values={values} props={props} onSubmit={onSubmit} />
        )}
      </div>
      <div className='d-flex align-items-center gap-3'>
        <div className='z-1'>
          <div className='document-prospecting-step-icon-wrapper d-flex align-items-center justify-content-center'>
            <div className='document-prospecting-final-step-icon zocks-bg-color-gray-300 rounded-circle' />
          </div>
        </div>
        <Button variant='primary' onClick={handleSubmit} loading={submitLoading} disabled={isLoading}>
          {INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId) ? 'Update contact' : 'Create contact'}
        </Button>
      </div>
    </div>
  )
}

interface ProspectingDetailsFormContentProps {
  serviceId: IntegrationServiceId
  values: ProspectingDetailsFormValues
  props: DocumentProspectingDetailsFormProps
  onSubmit: (detailsFormValues: ProspectingDetailsFormValues) => void
}

const ProspectingDetailsFormContent = forwardRef<ProspectingDetailsFormRef, ProspectingDetailsFormContentProps>(
  ({ serviceId, values, props, onSubmit }, ref) => {
    const dispatch = useAppDispatch()

    const form = useForm<ProspectingDetailsFormValues>({ defaultValues: values })
    const { sections, handleSectionsLoaded, activeSection, setActiveSection } = useSectionNavigation()

    useImperativeHandle(ref, () => ({ submit: form.handleSubmit(onSubmit) }))

    useEffect(() => {
      dispatch(setSelectedKeys(getAllKeysInData(values.data, _PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID)))
    }, [dispatch, values])

    const handleActiveSectionIdChange = (id: string) => {
      const section = sections.find((s) => s.id === id)
      if (section) {
        setActiveSection(section)
      }
    }

    return (
      <FormProvider {...form}>
        {props.type === 'segmented' && values.type === 'segmented' ? (
          <SegmentedProspectingDetailsFormContent
            serviceId={serviceId}
            values={values}
            props={props}
            activeSectionId={activeSection?.id}
            onActiveSectionIdChange={handleActiveSectionIdChange}
            onSectionsLoaded={handleSectionsLoaded}
          />
        ) : (
          <UpdateExternalContactForm
            serviceId={serviceId}
            rootFieldId={_PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID}
            contactSchema={props.schema}
            previousContactValues={props.previousValues}
            contactValues={values.data}
            requiredSteps={{}}
            metadata={null}
            hidePreviousValues={true}
            hideRootFieldHeader={true}
            enableExplainer={false}
          />
        )}
      </FormProvider>
    )
  },
)

interface SegmentedProspectingDetailsFormContentProps {
  serviceId: IntegrationServiceId
  values: SegmentedProspectingDetailsFormValues
  props: SegmentedDocumentProspectingDetailsFormProps
  activeSectionId?: string
  onActiveSectionIdChange?: (id: string) => void
  onSectionsLoaded?: (sections: NavigationSidebarSection[]) => void
}

const SegmentedProspectingDetailsFormContent: React.FC<SegmentedProspectingDetailsFormContentProps> = ({
  serviceId,
  values,
  props,
  activeSectionId,
  onActiveSectionIdChange,
  onSectionsLoaded,
}) => {
  const dispatch = useAppDispatch()
  const selectedKeys = useAppSelector(selectSelectedKeys)
  const segmentLoadingStates = useAppSelector(selectSegmentLoadingStates)

  const errors = useAppSelector(selectDocumentProspectingIntegrationContactErrors)

  const [sectionErrors, setSectionErrors] = useState<Record<string, { hasErrors: boolean; errorCount: number }>>({})

  const handleValidationChange = useCallback((key: string, hasErrors: boolean, errorCount: number) => {
    setSectionErrors((prev) => {
      const current = prev[key]
      if (!current || current.hasErrors !== hasErrors) {
        return {
          ...prev,
          [key]: { hasErrors, errorCount },
        }
      }
      return prev
    })
  }, [])

  const { data } = values
  const { schema, previousValues } = props

  useEffect(() => {
    if (!data) {
      return
    }
    const fieldsToDeselectArray = Object.entries(schema).map(([key, panel]) =>
      gatherAllChildSuccessfulFields(data[key], key, panel, errors, Object.keys(data[key] ?? {}), _PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID),
    )
    dispatch(deselectKeys(fieldsToDeselectArray.flat()))
  }, [data, dispatch, errors, schema])

  const navigationSections: ClickNavigationSection[] = useMemo(() => {
    if (!data) {
      return []
    }

    return Object.entries(schema)
      .map(([key, panel]) => {
        return SegmentedExternalContactUpdateClickNavigationSection({
          serviceId,
          panel,
          panelKey: key,
          metadata: errors,
          contactSchema: schema,
          previousContactValues: previousValues,
          contactValues: data,
          rootFieldId: _PROSPECTING_DETAILS_FORM_ROOT_FIELD_ID,
          hidePreviousValues: true,
          hideRootFieldHeader: false,
          segmentLoadingStates,
          selectedKeys,
          initialValues: {},
          sectionError: sectionErrors[key],
          onValidationChange: handleValidationChange,
        })
      })
      .filter((section) => section !== null) as ClickNavigationSection[]
  }, [schema, serviceId, errors, previousValues, data, segmentLoadingStates, selectedKeys, sectionErrors, handleValidationChange])

  useEffect(() => {
    if (onSectionsLoaded && navigationSections?.length > 0) {
      onSectionsLoaded(navigationSections)
    }
  }, [navigationSections, onSectionsLoaded])

  const activeIdToUse = activeSectionId || navigationSections[0]?.id

  const handleSectionClick = (id: string) => {
    if (onActiveSectionIdChange) {
      onActiveSectionIdChange(id)
    }
  }

  if (!data) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center zocks-py-4.0'>
        <span className='paragraph-medium'>Loading data…</span>
      </div>
    )
  }

  return (
    <div className='d-flex flex-column gap-3 w-100'>
      {errors ? (
        <div className='d-flex align-items-center gap-3 p-3 rounded-3 zocks-bg-color-warning'>
          <i className='bi bi-exclamation-triangle fs-5' />
          <p className='paragraph-medium m-0'>
            <span className='bold'>Contact partially saved. </span>
            <span>Some fields couldn't be synced to eMoney due to dependencies. Review sections marked with red and try again.</span>
          </p>
        </div>
      ) : null}
      <div className='flex-grow-1 overflow-auto d-flex flex-column'>
        <ClickNavigation
          sections={navigationSections}
          onSectionClick={handleSectionClick}
          defaultActiveSectionId={activeIdToUse}
          activeSectionId={activeIdToUse}
          className='h-100'
        />
      </div>
    </div>
  )
}
